(ns hai.home.day-pills
  "The pill row above Home's Progress timeline: the tasks due today, then the
   day's all-day calendar entries (birthdays, OOO, holidays). Shared by the
   desktop and mobile Progress cards, which differ only in how wide a pill may
   run — `compact?` picks the narrower cap.

   Both groups render through the one chip pattern in
   [[hai.ui.components.chip]], the deadline group in its warning variant: a
   deadline is an obligation and shouldn't read as the same kind of thing as a
   birthday. The group's label carries that difference in words too, so it
   holds up without colour.

   Every pill shows — the row doesn't cap or collapse. All-day entries can pile
   up several deep on a busy day (which is why they were taken off the timeline
   band to begin with, see [[hai.home.calendar]]), so this row can grow tall;
   showing everything was the deliberate call over hiding some behind a
   control."
  (:require
   [hai.home.calendar :as calendar]
   [hai.tasks.deadline :as deadline]
   [hai.ui.components.chip :as chip-ui]
   [hai.ui.components.datetime :as datetime]
   [hai.web-app.routing :as routing]
   [lambdaisland.ornament :as o]))

(defn- deadline-pill
  "One task due today. The label is a real anchor to the task, so it can be
   opened in a new tab like the calendar pills beside it. The clock rides the
   group's label rather than every pill — repeating it once per task says
   nothing the label hasn't already said."
  [task]
  [chip-ui/entity-chip
   {:variant :warning
    :label   [:a.day-pill-link
              {:href   (routing/href :tasks/detail {:task/uuid (:task/uuid task)})
               :target routing/link-target
               ;; Title first, like the calendar pills — a long one truncates in
               ;; the capsule and the tooltip is the only way to read it. The
               ;; deadline label trails it for a timed deadline's time of day.
               :title  (str (:task/title task) " — " (deadline/label task))}
              (:task/title task)]}])

(def ^:private birthday-pattern
  "Birthdays arrive as \"<name>'s birthday\" — Google lower-cases it, Outlook
   capitalises it, either apostrophe shows up, and a name already ending in s
   drops the second one. Nothing in the sync marks an entry as a birthday (no
   event-type attribute reaches Datomic), so the title is all there is to read."
  #"^(.+?)['’]s?\s+[Bb]irthday$")

(defn- birthday-name
  "The person's name when `title` is a calendar birthday, else nil. Deliberately
   narrow — anything not exactly this shape keeps its title untouched, so a
   non-English calendar or an event merely mentioning a birthday still reads the
   way it always did."
  [title]
  (second (re-matches birthday-pattern (or title ""))))

(defn- calendar-pill
  "One all-day calendar entry; opens the event in the provider's calendar when
   the entry carries a link, otherwise it's display-only. A birthday shows the
   cake and the person's name — the word adds nothing next to the glyph, and
   dropping it leaves room for the name the pill is actually about."
  [entry]
  (let [title  (calendar/m-title entry)
        link   (calendar/m-link entry)
        person (birthday-name title)
        ;; The untrimmed title stays on the tooltip and the accessible name, so
        ;; nothing is lost for a screen reader or a truncated pill.
        attrs  (cond-> {:title title}
                 person (assoc :aria-label title))]
    [chip-ui/entity-chip
     (cond-> {:label (if link
                       [:a.day-pill-link
                        (merge attrs {:href link :target "_blank" :rel "noopener noreferrer"})
                        (or person title)]
                       [:span attrs (or person title)])}
       person (assoc :glyph [:span.day-pill-emoji {:aria-hidden "true"} "🎂"]))]))

;; Its own full-width line under the counts sentence, so the two labelled groups
;; never interleave with it on a narrow card.
(o/defstyled day-pills :div
  {:flex-basis "100%"
   :display "flex" :flex-wrap "wrap" :align-items "center"
   :column-gap "6px" :row-gap "4px"}
  [:.day-pill-label
   {:display "inline-flex" :align-items "center" :gap "4px"
    :font-weight "600"}
   [:svg {:flex "0 0 auto" :opacity "0.85"}]]
  [chip-ui/entity-chip {:max-width "280px"}]
  [:.day-pill-link
   {:color "inherit" :text-decoration "none"}
   [:&:hover {:text-decoration "underline" :text-underline-offset "2px"}]]
  ;; The chip's glyph slot is sized for an svg; an emoji needs its own sizing.
  [:.day-pill-emoji {:font-size "12px" :line-height "1"}]
  ;; Desktop: each group is a flex item of the row, with its label and pills
  ;; forming one wrapping flex line inside it (pills stay layout-transparent, so
  ;; label + pills share the group's flex context). Two groups sit side by side
  ;; when they fit; when they don't, the whole "All day" group — label glued to
  ;; its pills — wraps to the next line as a unit, so the label can never strand
  ;; at the end of the deadline row.
  [:.day-pill-group
   {:display "flex" :flex-wrap "wrap" :align-items "center"
    :column-gap "6px" :row-gap "4px"}]
  [:.day-pill-pills {:display "contents"}]
  ;; Mobile: stack the two groups, and lay each one out as its own two-column
  ;; row — label, then that group's pills wrapping in the remaining width. So a
  ;; wrapped pill lines up under the group's own first pill, not back under the
  ;; label. The groups stay independent: tasks align with tasks, calendar events
  ;; with calendar events; their columns need not match. Pills also truncate
  ;; sooner and the deadline label tightens up ("Due:").
  [:&.compact
   {:display "flex" :flex-direction "column" :align-items "stretch" :row-gap "6px"}
   [chip-ui/entity-chip {:max-width "230px"}]
   [:.day-pill-label {:gap "2px"}]
   [:.day-pill-group
    {:display "grid" :grid-template-columns "auto 1fr"
     :align-items "baseline" :column-gap "8px"}]
   [:.day-pill-pills
    {:display "flex" :flex-wrap "wrap" :align-items "center"
     :column-gap "6px" :row-gap "4px"}]]
  ([{:keys [deadlines all-day compact?]}]
   ;; Each group is a `.day-pill-group` wrapping its label and a `.day-pill-pills`
   ;; box of pills. Desktop lays the groups out as flex items that wrap as whole
   ;; units on one row; mobile stacks them, each its own label + wrapping-pills
   ;; column — see the styles. The first child must stay a concrete element,
   ;; never a bare `:<>`: Ornament's fragment expansion drops every sibling
   ;; after a leading fragment (see expand-hiccup-tag-simple).
   (into [:<> {:class (when compact? "compact")}]
         (concat
          (when (seq deadlines)
            [[:div.day-pill-group
              [:span.day-pill-label [datetime/clock-icon] (if compact? "Due:" "Due today:")]
              (into [:div.day-pill-pills]
                    (for [task deadlines]
                      ^{:key (:task/uuid task)}
                      [deadline-pill task]))]])
          (when (seq all-day)
            [[:div.day-pill-group
              [:span.day-pill-label "All day:"]
              (into [:div.day-pill-pills]
                    (for [entry all-day]
                      ^{:key (:journal.entry/uuid entry)}
                      [calendar-pill entry]))]])))))
